// src/components/Layout.jsx
//
// What changed from the last version:
//   - The sidebar's floating collapse/expand circle is GONE. That button
//     now lives in the header instead (top-left, right where the sidebar
//     edge meets the header). Since the header itself never unmounts and
//     always renders, this button is now ALWAYS in the same place and
//     ALWAYS visible — collapsed or expanded, mobile or desktop — instead
//     of disappearing or jumping around with the sidebar.
//   - FULL_W / ICON_W kept in sync with Sidebar.jsx (224 / 72).

import React, { Suspense, useRef, useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { User, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import Sidebar from "./Sidebar";
import AppTourModal from "./AppTour/AppTourModal";
import useAppStore from "../store/useAppStore";
import { useStoreDetails }   from "../hooks/useStoreDetails";
import { useActivityTicker } from "../hooks/useActivityTicker";
import { useScrollReset }    from "../hooks/useScrollReset";
import { useScrollTop }      from "../hooks/useScrollTop";
// Marketplace switching is disabled for the eBay-only application.
// import axiosInstance from "../utils/axiosInstance";

const FULL_W = 252;
const ICON_W = 78;

const C = {
  sbDark:       "#0e0a24",
  sbDark2:      "#1d1442",
  accent:       "#8b6bf2",
  accentSoft:   "rgba(139,107,242,0.14)",
  accentBorder: "rgba(139,107,242,0.28)",
  textPrimary:  "#f3f0ff",
  textMuted:    "rgba(200,193,235,0.6)",
  contentBg:    "#eef1fa",
};

// ── Content-area skeleton — shown only inside the Outlet, never fullscreen ──
const ContentSkeleton = () => (
 <div style={{ padding: "16px 20px" }}>
    <div style={{
      height: 28, width: "35%", borderRadius: 6,
      background: "linear-gradient(90deg, #dde1f0 25%, #e8eaf4 50%, #dde1f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s infinite",
      marginBottom: 20,
    }} />
    {[1, 2, 3].map(i => (
      <div key={i} style={{
        height: 72, borderRadius: 10,
        background: "linear-gradient(90deg, #dde1f0 25%, #e8eaf4 50%, #dde1f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.4s infinite",
        marginBottom: 14,
        animationDelay: `${i * 0.1}s`,
      }} />
    ))}
    <style>{`
      @keyframes shimmer {
        0%   { background-position: 200% center; }
        100% { background-position: -200% center; }
      }
    `}</style>
  </div>
);

// ── Header collapse toggle — fixed position, always visible, never hidden ──
const SidebarToggle = ({ open, onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
      title={open ? "Collapse sidebar" : "Expand sidebar"}
      style={{
        width: 34, height: 34,
        borderRadius: 9,
        border: `1px solid ${hover ? C.accentBorder : "rgba(255,255,255,0.1)"}`,
        background: hover ? "rgba(139,107,242,0.16)" : "rgba(255,255,255,0.05)",
        color: "#c4b6fb",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer",
        flexShrink: 0,
        transition: "background 0.15s ease, border-color 0.15s ease, transform 0.15s ease",
        transform: hover ? "scale(1.05)" : "scale(1)",
      }}
    >
      {open ? <PanelLeftClose size={17} /> : <PanelLeftOpen size={17} />}
    </button>
  );
};

const Layout = () => {
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const { sidebarOpen, setSidebarOpen } = useAppStore();
  const { storeName }   = useStoreDetails();
  const { currentActivity } = useActivityTicker();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showTour, setShowTour] = useState(
    () => !localStorage.getItem("appTourShown")
  );
  // Marketplace switching is intentionally disabled. Keep the previous state
  // and API implementation commented in history below for future multi-channel support.
  // const marketplaceSettings = { marketplace: "EBAY", showMarketplaceSwitcher: false };
  // const [marketplaceSettings, setMarketplaceSettings] = useState({ marketplace: "EBAY", showMarketplaceSwitcher: true });
  // const [marketplaceSwitching, setMarketplaceSwitching] = useState(false);

  useScrollReset(contentRef);
  const { showScrollTop } = useScrollTop(contentRef);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* Marketplace API calls intentionally disabled for eBay-only mode:
  useEffect(() => { ... marketplaceSwitcherVisibilityChanged ... }, []);
  useEffect(() => { ... GET /wix/oauth/marketplaceSettings ... }, []);
  const handleMarketplaceChange = async (event) => { ... POST /wix/oauth/switchMarketplace ... };
  */

  const sidebarW = isMobile ? 0 : (sidebarOpen ? FULL_W : ICON_W);

  const [profileHover, setProfileHover] = useState(false);
  const [scrollTopHover, setScrollTopHover] = useState(false);

  return (
    <div style={{ display: "flex", minHeight: "100vh", backgroundColor: C.sbDark }}>

      {/* ── SIDEBAR — never unmounted; no toggle button inside it anymore ── */}
      <Sidebar />

      <div style={{
        flex: 1,
        marginLeft: sidebarW,
        transition: "margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
      }}>

        {/* ── HEADER — never unmounted, seamless with sidebar, owns the
              collapse toggle so it's permanently visible in one spot ── */}
        <header style={{
          height:         52,
          background:     `linear-gradient(90deg, ${C.sbDark} 0%, ${C.sbDark2} 100%)`,
          color:          "#fff",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          padding:        "0 18px",
          position:       "fixed",
          top: 0,
          left:           sidebarW,
          right:          0,
          zIndex:         1500,
          boxShadow:      "0 4px 14px -6px rgba(0,0,0,0.35)",
          transition:     "left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          boxSizing:      "border-box",
        }}>

          {!isMobile && (
            <SidebarToggle open={sidebarOpen} onClick={() => setSidebarOpen(!sidebarOpen)} />
          )}
          {isMobile && <span style={{ width: 34 }} />}

          {currentActivity && (
            <div style={{
              position:     "absolute",
              left:         "50%",
              transform:    "translateX(-50%)",
              maxWidth:     500,
              overflow:     "hidden",
              whiteSpace:   "nowrap",
              textOverflow: "ellipsis",
              fontSize:     13,
              color:        "#c4b6fb",
              padding:      "5px 13px",
              background:   C.accentSoft,
              border:       `1px solid ${C.accentBorder}`,
              borderRadius: 20,
              display:      "flex",
              alignItems:   "center",
              gap:          7,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: C.accent, flexShrink: 0,
              }} />
              {currentActivity}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Marketplace switcher intentionally hidden; retain the original
                select implementation in git history for future marketplaces.
            {marketplaceSettings.showMarketplaceSwitcher && (
              <label style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <select
                  value={marketplaceSettings.marketplace}
                  onChange={handleMarketplaceChange}
                  disabled={marketplaceSwitching}
                  aria-label="Switch marketplace"
                  style={{
                    appearance: "none",
                    minWidth: 112,
                    padding: "8px 27px 8px 11px",
                    borderRadius: 9,
                    border: `1px solid ${C.accentBorder}`,
                    background: C.accentSoft,
                    color: "#e8e2ff",
                    fontSize: 12,
                    fontWeight: 600,
                    outline: "none",
                    cursor: marketplaceSwitching ? "wait" : "pointer",
                  }}
                >
                  <option value="EBAY">eBay</option>
                  <option value="ETSY">Etsy</option>
                  <option value="AMAZON">Amazon</option>
                </select>
                <span aria-hidden="true" style={{ position: "absolute", right: 10, color: "#c4b6fb", pointerEvents: "none" }}>⌄</span>
              </label>
            )} */}

            <div
              onClick={() => navigate("/user-profile")}
              onMouseEnter={() => setProfileHover(true)}
              onMouseLeave={() => setProfileHover(false)}
              style={{
                display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                background: profileHover ? "rgba(139,107,242,0.24)" : C.accentSoft,
                border: `1px solid ${C.accentBorder}`, borderRadius: 9, padding: "6px 11px",
                transition: "background 0.15s ease",
              }}
            >
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(139,107,242,0.22)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <User size={14} color={C.accent} />
              </div>
              {storeName && <span style={{ fontSize: 12.5, fontWeight: 500, color: "#dcd3fc" }}>{storeName}</span>}
            </div>
          </div>
        </header>

        {/* ── CONTENT — Suspense boundary scoped to this area only ── */}
        <div
          ref={contentRef}
          style={{
            flex:            1,
            marginTop:       52,
            backgroundColor: C.contentBg,
            boxSizing:       "border-box",
            overflowY:       "auto",
            height:          "calc(100vh - 52px)",
          }}
        >
          <Suspense fallback={<ContentSkeleton />}>
            <div style={{ padding: "16px 20px" }}>
              <Outlet context={{ sidebarOpen }} />
            </div>
          </Suspense>
        </div>

        {/* ── FOOTER — never unmounted ── */}
        <footer style={{
          backgroundColor: "#dde1f0",
          color:           "#5a5080",
          textAlign:       "center",
          padding:         "12px",
          fontSize:        13,
          borderTop:       "0.5px solid #c8cce0",
          flexShrink:      0,
        }}>
          Copyright © 2025 EcstaCommerce. All Rights Reserved.
        </footer>
      </div>

      {/* ── SCROLL TO TOP ── */}
      {showScrollTop && (
        <button
          onClick={() => contentRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
          onMouseEnter={() => setScrollTopHover(true)}
          onMouseLeave={() => setScrollTopHover(false)}
          style={{
            position:  "fixed",
            bottom:    40,
            right:     30,
            width:     42,
            height:    42,
            borderRadius: "50%",
            border:    "none",
            background: "linear-gradient(135deg,#7c5cf0,#6645d8)",
            color:     "#fff",
            fontSize:  18,
            cursor:    "pointer",
            zIndex:    9999,
            display:   "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: scrollTopHover ? "0 8px 20px -4px rgba(124,92,240,0.55)" : "0 5px 14px -3px rgba(124,92,240,0.45)",
            transform: scrollTopHover ? "scale(1.08)" : "scale(1)",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
          }}
        >↑</button>
      )}

      {/* ── APP TOUR ── */}
      {showTour && (
        <AppTourModal onClose={() => {
          localStorage.setItem("appTourShown", "1");
          setShowTour(false);
        }} />
      )}
    </div>
  );
};

export default Layout;
